import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NodeProps } from 'reactflow';
import { store } from 'src/redux/store';
import { setNodeConfiguration } from 'src/redux/workflows/slice';
import { useSelector } from 'react-redux';
import { getWorkflows } from 'src/redux/workflows/selectors';
import { formatActionTypeKey, getActionIcon, getActionLabel } from 'src/utils/general';
import { isRecordContactRecipient, recordContactLabel, supportsRecordContact } from 'src/utils/workflowRecipients';
import WorkflowNodeCard from '../WorkflowNodeCard/WorkflowNodeCard';
import cardStyles from '../WorkflowNodeCard/WorkflowNodeCard.module.scss';
import ActionSelectionOverlay, { ActionFormData } from './ActionSelectionOverlay/ActionSelectionOverlay';

const ActionNode: React.FC<NodeProps> = ({ id, data }) => {
  const { t } = useTranslation();
  const triggerModel = useSelector(getWorkflows).selectedTrigger?.model;
  const [actionData, setActionData] = useState<ActionFormData | null>(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Restore from existing data when viewing an existing workflow
  useEffect(() => {
    if (data.actionData && !actionData) {
      const restored: ActionFormData = {
        actionType: data.actionData.actionType,
        note: '',
        subject: data.actionData.title || '',
        title: data.actionData.title || '',
        content: data.actionData.content || data.actionData.returnValue || '',
        message: data.actionData.message || data.actionData.returnValue || '',
        selectedProductId: data.actionData.selectedProductId,
        unit: data.actionData.unit,
        amount: data.actionData.amount,
        partnerId: data.actionData.partnerId,
        partnerName: data.actionData.partnerName,
        recipient: data.actionData.recipient,
      };
      setActionData(restored);
    }
  }, [data.actionData]);

  const handleActionSelect = (formData: ActionFormData) => {
    if (!data.isEditMode) return;

    setActionData(formData);

    let returnValue = formData.content || formData.message || '';
    if (formData.selectedProductId !== undefined && formData.selectedProductId !== null) {
      returnValue = String(formData.selectedProductId);
    }

    store.dispatch(
      setNodeConfiguration({
        nodeId: id,
        nodeType: 'action',
        data: {
          actionType: formData.actionType,
          title: formData.subject || formData.title || formData.actionType,
          returnValue,
          content: formData.content,
          message: formData.message,
          selectedProductId: formData.selectedProductId,
          unit: formData.unit,
          amount: formData.amount,
          partnerId: formData.partnerId,
          partnerName: formData.partnerName,
          recipient: formData.recipient,
        },
      }),
    );
  };

  const handleDeleteClick = () => {
    if (data.isEditMode && data.removeNode) {
      data.removeNode(id);
    } else if (data.isEditMode && data.removeLastNode) {
      data.removeLastNode();
      data.removeLastNode();
    }
  };

  const actionSummary = () => {
    if (!actionData) return undefined;
    const rows: { label: string; value: string }[] = [];
    const subject = actionData.subject || actionData.title;
    const isMail = formatActionTypeKey(actionData.actionType || '') === 'mail';
    if (isMail && isRecordContactRecipient(actionData.recipient) && supportsRecordContact(triggerModel)) {
      rows.push({ label: t('workflows.summary_recipient'), value: recordContactLabel(triggerModel, t) });
    } else if (actionData.recipient && !isRecordContactRecipient(actionData.recipient)) {
      rows.push({ label: t('workflows.summary_recipient'), value: actionData.recipient });
    }
    if (actionData.partnerName) rows.push({ label: t('workflows.summary_recipient'), value: actionData.partnerName });
    if (subject && subject !== actionData.actionType)
      rows.push({ label: t('workflows.summary_subject'), value: subject });
    if (actionData.amount) {
      rows.push({
        label: t('workflows.summary_amount'),
        value: actionData.unit
          ? t(`workflows.amount_per_${actionData.unit}`, {
              amount: actionData.amount,
              defaultValue: String(actionData.amount),
            })
          : String(actionData.amount),
      });
    }
    if (!rows.length) return undefined;
    return (
      <dl className={cardStyles.keyValues}>
        {rows.map(row => (
          <React.Fragment key={`${row.label}-${row.value}`}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    );
  };

  const handleCardClick = () => {
    setIsOverlayOpen(true);
  };

  return (
    <WorkflowNodeCard
      id={id}
      label={t('workflows.node_label_action')}
      title={t('workflows.action_overlay.execute')}
      cardText={t('workflows.action_overlay.choose_action')}
      isEditMode={data.isEditMode}
      isSelected={!!actionData}
      selectedTitle={actionData ? getActionLabel(actionData.actionType) : undefined}
      selectedSubtitle={t('workflows.actions')}
      selectedIcon={actionData ? getActionIcon(actionData.actionType) : undefined}
      summary={actionSummary()}
      onCardClick={handleCardClick}
      onResetClick={handleDeleteClick}>
      <ActionSelectionOverlay
        isOpen={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
        onActionSelect={handleActionSelect}
        initialData={actionData}
      />
    </WorkflowNodeCard>
  );
};

export default ActionNode;
